import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import { EyeIcon } from '@heroicons/react/24/outline';
import { loadBuildingsForSelector } from '../../../api';
import { feeCategoriesApi } from '../../../api/feeCategoriesApi';
import { useAuth } from '../../../contexts/AuthContext';
import {
  applyDefaultBuildingSelection,
  needsBuildingSelection,
  selectBuilding,
  shouldShowBuildingSelector,
} from '../../../utils/buildingSelection';
import {
  paymentApi,
  ReceivablePeriodDetailRow,
} from '../../../api/paymentApi';
import { FeeCategoryPillSelect } from './FeeCategoryPillSelect';
import ReceivableCustomerLedgerDetail from './ReceivableCustomerLedgerDetail';
import BillingPeriodMonthPicker from '../../../components/common/BillingPeriodMonthPicker';
import {
  billingPeriodToFilenameKy,
  downloadExcel,
  sanitizeExportFilename,
} from '../../../utils/excelExport';
import { getCurrentBillingPeriodValue } from '../../../utils/billingPeriodPicker';
import { buildReceivableDebtPeriodSummary } from '../receivableDebtPeriodSummary';
const PAGE_SIZE_OPTIONS = [20, 50, 100];

export interface ReceivableDebtSummaryTabHandle {
  exportReport: () => Promise<void>;
}

const formatLedgerMoney = (value: number) => {
  const rounded = Math.round(value);
  if (rounded === 0) return '—';
  return `${new Intl.NumberFormat('vi-VN').format(rounded)}đ`;
};

const periodCreditAmount = (row: ReceivablePeriodDetailRow) =>
  row.totalPayment + row.offset + row.preExpenditure;

const MoneyCell: React.FC<{ value: number; strong?: boolean }> = ({ value, strong }) => (
  <td
    className={`px-3 py-2.5 text-right text-sm border-b border-gray-100 whitespace-nowrap ${
      strong ? 'font-semibold bg-gray-50' : ''
    }`}
  >
    {formatLedgerMoney(value)}
  </td>
);

export const ReceivableDebtSummaryTab = forwardRef<ReceivableDebtSummaryTabHandle>(
  function ReceivableDebtSummaryTab(_props, ref) {
    const { user } = useAuth();
    const [buildings, setBuildings] = useState<Array<{ id: string; name: string }>>([]);
    const [buildingId, setBuildingId] = useState('');
    const [feeCategories, setFeeCategories] = useState<Array<{ id: string; name: string }>>([]);
    const [feeCategoryId, setFeeCategoryId] = useState('');
    const [apartmentSearch, setApartmentSearch] = useState('');
    const [collectionPeriod, setCollectionPeriod] = useState(getCurrentBillingPeriodValue());
    const [rows, setRows] = useState<ReceivablePeriodDetailRow[]>([]);
    const [totals, setTotals] = useState<ReceivablePeriodDetailRow | null>(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalRows, setTotalRows] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [jumpPage, setJumpPage] = useState('1');
    const [customerDetail, setCustomerDetail] = useState<ReceivablePeriodDetailRow | null>(null);

    useEffect(() => {
      loadBuildingsForSelector().then((data) => {
        const list = data || [];
        setBuildings(list);
        setBuildingId((prev) => applyDefaultBuildingSelection(list, user, prev));
      });
    }, [user]);

    useEffect(() => {
      if (!buildingId) {
        setFeeCategories([]);
        setFeeCategoryId('');
        return;
      }
      feeCategoriesApi
        .getAll(buildingId)
        .then((data) =>
          setFeeCategories(
            (data || [])
              .filter((category) => category.isActive)
              .map((category) => ({ id: category.id, name: category.name })),
          ),
        )
        .catch(() => setFeeCategories([]));
    }, [buildingId]);

    const loadReport = useCallback(async () => {
      if (!buildingId) return;
      setLoading(true);
      try {
        const data = await paymentApi.getReceivableStatisticsDetail({
          buildingId,
          collectionPeriod,
          feeCategoryId: feeCategoryId || undefined,
          apartmentSearch: apartmentSearch.trim() || undefined,
          page,
          limit: pageSize,
        });
        setRows(data.items || []);
        setTotals(data.totals ?? null);
        setTotalRows(data.total ?? data.items?.length ?? 0);
      } catch {
        toast.error('Không thể tải báo cáo tổng hợp công nợ');
        setRows([]);
        setTotals(null);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    }, [
      apartmentSearch,
      buildingId,
      collectionPeriod,
      page,
      pageSize,
      feeCategoryId,
    ]);

    useEffect(() => {
      setPage(1);
      setJumpPage('1');
    }, [buildingId, collectionPeriod, feeCategoryId, apartmentSearch, pageSize]);

    useEffect(() => {
      if (!buildingId) return;
      const timer = setTimeout(loadReport, 300);
      return () => clearTimeout(timer);
    }, [loadReport, buildingId]);

    const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
    const pageStart = totalRows === 0 ? 0 : (page - 1) * pageSize + 1;
    const pageEnd = totalRows === 0 ? 0 : Math.min(page * pageSize, totalRows);

    const buildingName = buildings.find((b) => b.id === buildingId)?.name || 'chung-cu';

    const periodSummary = useMemo(
      () => buildReceivableDebtPeriodSummary(totals, collectionPeriod),
      [totals, collectionPeriod],
    );

    const exportReport = useCallback(async () => {
      if (!buildingId) {
        toast.error('Vui lòng chọn chung cư');
        return;
      }
      try {
        const data = await paymentApi.exportReceivableStatisticsDetail({
          buildingId,
          collectionPeriod,
          feeCategoryId: feeCategoryId || undefined,
          apartmentSearch: apartmentSearch.trim() || undefined,
        });
        const exportRows = (data.items || []).filter((row) => Boolean(row.apartmentId?.trim()));
        const totalsRow = data.totals;
        if (exportRows.length === 0 && !totalsRow) {
          toast.error('Không có dữ liệu để xuất');
          return;
        }
        const headers = [
          'Mã căn hộ',
          'Tên khách hàng',
          'Nợ đầu kỳ',
          'Có đầu kỳ',
          'Phát sinh nợ',
          'Phát sinh có',
          'Nợ cuối kỳ',
          'Có cuối kỳ',
        ];
        const sheetRows: (string | number)[][] = [];
        if (totalsRow) {
          sheetRows.push([
            'Tổng cộng',
            '',
            totalsRow.openingDebit,
            totalsRow.openingCredit,
            totalsRow.totalIncurred,
            periodCreditAmount(totalsRow),
            totalsRow.closingDebit,
            totalsRow.closingCredit,
          ]);
        }
        sheetRows.push(
          ...exportRows.map((row) => [
            row.apartmentCode,
            row.ownerName,
            row.openingDebit,
            row.openingCredit,
            row.totalIncurred,
            periodCreditAmount(row),
            row.closingDebit,
            row.closingCredit,
          ]),
        );
        const feeCategoryLabel =
          feeCategories.find((c) => c.id === feeCategoryId)?.name ?? '';
        const exportSummary = buildReceivableDebtPeriodSummary(totalsRow, collectionPeriod);
        const metaRows: (string | number)[][] = [
          ['BÁO CÁO TỔNG HỢP CÔNG NỢ'],
          ['Tòa nhà', buildingName],
          ['Kỳ báo cáo', collectionPeriod],
          ...(feeCategoryLabel ? [['Loại phí', feeCategoryLabel] as (string | number)[]] : []),
          ...(apartmentSearch.trim()
            ? [['Căn hộ lọc', apartmentSearch.trim()] as (string | number)[]]
            : []),
          [],
          [`BÁO CÁO CÔNG NỢ KỲ ${collectionPeriod}`],
          ...(exportSummary
            ? [
                [`Công nợ kỳ ${exportSummary.previousPeriod}`, exportSummary.previousPeriodDebt],
                [`Phát sinh kỳ ${exportSummary.collectionPeriod}`, exportSummary.periodIncurred],
                [`Phải thu trong ${exportSummary.collectionPeriod}`, exportSummary.receivableInPeriod],
                [`Đã thu trong ${exportSummary.collectionPeriod}`, exportSummary.periodCollected],
                [`Chưa thu trong ${exportSummary.collectionPeriod}`, exportSummary.periodUncollected],
              ]
            : []),
          [],
          [`BÁO CÁO CHI TIẾT CÔNG NỢ KỲ ${collectionPeriod}`],
          [],
        ];
        const buildingPart = sanitizeExportFilename(buildingName) || 'chung-cu';
        const kyPart = billingPeriodToFilenameKy(collectionPeriod);
        const filename = `${sanitizeExportFilename(
          `bao-cao-tong-hop-cong-no_${buildingPart}_${kyPart}`,
        )}.xlsx`;
        const boldRowIndices = totalsRow ? [0] : [];
        const boldMetaRowIndices = [
          0,
          metaRows.findIndex((row) => String(row[0]).startsWith('BÁO CÁO CÔNG NỢ KỲ')),
          metaRows.findIndex((row) => String(row[0]).startsWith('BÁO CÁO CHI TIẾT')),
        ].filter((index) => index >= 0);
        downloadExcel('Báo cáo', headers, sheetRows, filename, {
          metaRows,
          boldMetaRowIndices,
          boldRowIndices,
          rightAlignColumnIndices: [2, 3, 4, 5, 6, 7],
        });
        toast.success('Đã xuất báo cáo');
      } catch {
        toast.error('Không thể xuất báo cáo');
      }
    }, [
      apartmentSearch,
      buildingId,
      buildingName,
      collectionPeriod,
      feeCategoryId,
      feeCategories,
    ]);

    useImperativeHandle(ref, () => ({ exportReport }), [exportReport]);

    const renderRow = (row: ReceivablePeriodDetailRow, options?: { total?: boolean }) => (
      <tr
        key={options?.total ? 'total' : row.apartmentId}
        className={options?.total ? 'bg-gray-50 font-semibold' : 'hover:bg-gray-50/80'}
      >
        <td className="px-3 py-2.5 text-sm border-b border-gray-100">
          {options?.total ? 'Tổng cộng' : row.apartmentCode}
        </td>
        <td className="px-3 py-2.5 text-sm border-b border-gray-100 max-w-[200px] truncate">
          {options?.total ? '' : row.ownerName}
        </td>
        <MoneyCell value={row.openingDebit} strong={options?.total} />
        <MoneyCell value={row.openingCredit} strong={options?.total} />
        <MoneyCell value={row.totalIncurred} strong={options?.total} />
        <MoneyCell value={periodCreditAmount(row)} strong={options?.total} />
        <MoneyCell value={row.closingDebit} strong={options?.total} />
        <MoneyCell value={row.closingCredit} strong={options?.total} />
        <td className="px-3 py-2.5 text-center border-b border-gray-100">
          {!options?.total && (
            <button
              type="button"
              onClick={() => setCustomerDetail(row)}
              className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 text-gray-500 hover:text-cyan-700 hover:border-cyan-300"
              title="Chi tiết công nợ theo khách hàng"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
          )}
        </td>
      </tr>
    );

    if (customerDetail) {
      return (
        <ReceivableCustomerLedgerDetail
          buildingId={buildingId}
          apartmentId={customerDetail.apartmentId}
          apartmentCode={customerDetail.apartmentCode}
          ownerName={customerDetail.ownerName}
          collectionPeriod={collectionPeriod}
          feeCategoryId={feeCategoryId}
          onFeeCategoryChange={setFeeCategoryId}
          onCollectionPeriodChange={setCollectionPeriod}
          feeCategories={feeCategories}
          onBack={() => setCustomerDetail(null)}
        />
      );
    }

    const goToPage = (next: number) => {
      const clamped = Math.min(totalPages, Math.max(1, next));
      setPage(clamped);
      setJumpPage(String(clamped));
    };

    return (
      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex flex-wrap items-end gap-3">
            {shouldShowBuildingSelector(buildings) && (
              <select
                value={buildingId}
                onChange={(e) => selectBuilding(user?.id, e.target.value, setBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm min-w-[160px]"
              >
                {buildings.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            )}
            <FeeCategoryPillSelect
              label="Loại phí"
              value={feeCategoryId}
              options={feeCategories}
              onChange={setFeeCategoryId}
            />
            <input
              type="text"
              value={apartmentSearch}
              onChange={(e) => setApartmentSearch(e.target.value)}
              placeholder="Căn hộ"
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm min-w-[140px]"
            />
            <div className="flex flex-col gap-1">
              <span className="text-xs text-gray-500">Kỳ báo cáo</span>
              <BillingPeriodMonthPicker
                value={collectionPeriod}
                onChange={setCollectionPeriod}
              />
            </div>
          </div>
        </div>

        {periodSummary && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-bold uppercase text-gray-900 tracking-wide">
              Báo cáo công nợ kỳ {periodSummary.collectionPeriod}
            </h3>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
              {[
                {
                  label: `Công nợ kỳ ${periodSummary.previousPeriod}`,
                  value: periodSummary.previousPeriodDebt,
                },
                {
                  label: `Phát sinh kỳ ${periodSummary.collectionPeriod}`,
                  value: periodSummary.periodIncurred,
                },
                {
                  label: `Phải thu trong ${periodSummary.collectionPeriod}`,
                  value: periodSummary.receivableInPeriod,
                },
                {
                  label: `Đã thu trong ${periodSummary.collectionPeriod}`,
                  value: periodSummary.periodCollected,
                },
                {
                  label: `Chưa thu trong ${periodSummary.collectionPeriod}`,
                  value: periodSummary.periodUncollected,
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2.5"
                >
                  <div className="text-xs text-gray-500">{item.label}</div>
                  <div className="text-lg font-semibold text-gray-900 tabular-nums mt-0.5">
                    {formatLedgerMoney(item.value)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <h3 className="px-4 py-3 text-sm font-bold uppercase text-gray-900 tracking-wide border-b border-gray-200 bg-gray-50/50">
            Báo cáo chi tiết công nợ kỳ {collectionPeriod}
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="text-xs font-semibold uppercase text-gray-600 bg-gray-50 border-b border-gray-200">
                  <th rowSpan={2} className="px-3 py-3 text-left align-bottom">Mã căn hộ</th>
                  <th rowSpan={2} className="px-3 py-3 text-left align-bottom">Tên khách hàng</th>
                  <th colSpan={2} className="px-3 py-2 text-center border-l border-gray-200">
                    Số dư đầu kỳ
                  </th>
                  <th colSpan={2} className="px-3 py-2 text-center border-l border-gray-200">
                    Phát sinh trong kỳ
                  </th>
                  <th colSpan={2} className="px-3 py-2 text-center border-l border-gray-200">
                    Số dư cuối kỳ
                  </th>
                  <th rowSpan={2} className="px-3 py-3 text-center border-l border-gray-200 align-bottom">
                    Thao tác
                  </th>
                </tr>
                <tr className="text-xs font-semibold uppercase text-gray-600 bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-right border-l border-gray-200">Nợ</th>
                  <th className="px-3 py-2 text-right">Có</th>
                  <th className="px-3 py-2 text-right border-l border-gray-200">Nợ</th>
                  <th className="px-3 py-2 text-right">Có</th>
                  <th className="px-3 py-2 text-right border-l border-gray-200">Nợ</th>
                  <th className="px-3 py-2 text-right">Có</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                      Đang tải...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                      Chưa có dữ liệu
                    </td>
                  </tr>
                ) : (
                  <>
                    {totals && renderRow(totals, { total: true })}
                    {rows.map((row) => renderRow(row))}
                  </>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-600 space-y-2">
            <div>
              {totalRows > 0 ? (
                <>
                  Đang xem{' '}
                  <span className="font-medium text-gray-800 tabular-nums">
                    {pageStart}–{pageEnd}
                  </span>{' '}
                  trong{' '}
                  <span className="font-medium text-gray-800 tabular-nums">{totalRows}</span> căn hộ
                  <span className="text-gray-400 mx-2">·</span>
                  <span className="font-medium text-gray-800 tabular-nums">
                    Trang {page} / {totalPages}
                  </span>
                </>
              ) : (
                'Tổng 0 căn hộ'
              )}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="rounded border border-gray-200 bg-white px-2 py-1 text-sm"
                aria-label="Số dòng mỗi trang"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size}/trang</option>
                ))}
              </select>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={page <= 1 || loading}
                    onClick={() => goToPage(page - 1)}
                    className="px-2.5 py-1 rounded border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40"
                    aria-label="Trang trước"
                  >
                    ‹
                  </button>
                  <span className="min-w-[5.5rem] text-center font-semibold text-gray-800 tabular-nums">
                    {page} / {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={page >= totalPages || loading}
                    onClick={() => goToPage(page + 1)}
                    className="px-2.5 py-1 rounded border border-gray-200 bg-white hover:bg-gray-100 disabled:opacity-40"
                    aria-label="Trang sau"
                  >
                    ›
                  </button>
                </div>
                <span className="flex items-center gap-1.5 text-gray-600">
                  Nhảy tới
                  <input
                    type="text"
                    inputMode="numeric"
                    value={jumpPage}
                    onChange={(e) => setJumpPage(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const parsed = Number(jumpPage);
                        if (parsed) goToPage(parsed);
                      }
                    }}
                    className="w-12 rounded border border-gray-200 px-2 py-1 text-center text-sm tabular-nums"
                    aria-label="Số trang"
                  />
                  <span className="text-gray-500 tabular-nums">/ {totalPages}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

export default ReceivableDebtSummaryTab;
